import { useState } from 'react';
import axios from "axios";
import { formatSizeToGb } from '../utils/fileManagement';
import config from "../config";

/**
 * Custom hook for managing user projects and storage calculations
 * Handles project fetching, storage usage tracking, and upload limits
 * 
 * @param {Function} getStrapiUser - Function to get authenticated Strapi user
 * @param {Function} setUsage - Function to update storage usage in context
 * @param {string} service - Current service type to filter projects
 * @param {Function} getCredentials - Function to fetch cloud credentials
 * @param {Function} retrieveProcessings - Function to fetch processing options
 * @param {Function} setMaxLinkCesium - Function to update max cesium links in context
 * @param {Function} setUsedCesiumLinks - Function to update used cesium links in context
 * @returns {Object} Project state and management functions
 */
export const useProjects = (getStrapiUser, setUsage, service, getCredentials, retrieveProcessings, setMaxLinkCesium, setUsedCesiumLinks) => {
    // Project management state
    const [projects, setProjects] = useState([]);                    // User's projects array
    const [projectSelected, setProjectSelected] = useState(null);    // Currently selected project
    const [userHasNoProject, setUserHasNoProject] = useState(true);  // Boolean flag for empty state
    const [GoUploadedThisMonth, setGoUploadedThisMonth] = useState(0); // Monthly upload usage in GB
    const [maxGoUploadPerMonth, setMaxGoUploadPerMonth] = useState(1); // Monthly upload limit in GB (set to 1 to avoid NaN)
    const [loading, setLoading] = useState(false);                   // Loading state for API operations

    /**
     * Counts the number of existing cesium links across all user projects
     * @param {Array} projects - Array of project objects
     * @returns {number} Total number of cesium links
     */
    const getCesiumLinksUsed = (projects) => {
        let linksCount = 0;
        for (let i = 0; i < projects.length; i += 1) {
            const proj = projects[i]?.attributes;
            const files = Array.isArray(proj?.data?.data) ? proj.data.data : [];
            for (let j = 0; j < files.length; j += 1) {
                const fileData = files[j];
                // Count files that have share_cesium_short_url data
                if (fileData?.attributes?.share_cesium_short_url?.data) {
                    linksCount += 1;
                }
            }
        }
        return linksCount;
    };

    /**
     * Fetches user projects from Strapi API with full related data
     * Also calculates storage usage, upload limits, and initializes credentials
     */
    const retrieveProjects = async () => {
        if (loading) return; // Prevent multiple simultaneous calls
        setLoading(true);
        try {
            // Get authenticated user and Strapi JWT
            const { jwtStrapi, userStrapi } = await getStrapiUser();
            let ids = [];
            let userProjects = [];

            // Extract project IDs from user profile and fetch full project data
            const userProjectsRaw = Array.isArray(userStrapi?.data?.projects) ? userStrapi.data.projects : [];
            //console.log(userProjectsRaw)
            if (userProjectsRaw.length > 0) {
                ids = userProjectsRaw.map(project => project.id).filter(Boolean);

                // Fetch projects with all related data (files, processing requests, etc.)
                let allProjects = [];
                let page = 1;
                let hasMoreData = true;
                while (hasMoreData) {
                    const res = await axios.get(`${config.apiUrl}/api/projects`, {
                        headers: {
                            Authorization: `Bearer ${jwtStrapi}`,
                        },
                        params: {
                            'populate[0]': 'data',
                            'populate[1]': 'data.results',
                            'populate[2]': 'data.processing_requests',
                            'populate[3]': 'processing_requests',
                            'populate[4]': 'data.processing_requests.processing',
                            'populate[5]': 'data.processing_requests.subprocessings',
                            'populate[6]': 'data.share_cesium_short_url',
                            'populate[7]': 'data.transformation_cesium_points',
                            'populate[8]': 'data.transformation_cesium_gs',
                            'pagination[page]': page,
                            'pagination[pageSize]': 100
                        }
                    });
                    const pageProjects = Array.isArray(res?.data?.data) ? res.data.data : [];
                    allProjects = allProjects.concat(pageProjects);
                    const pagination = res?.data?.meta?.pagination;
                    hasMoreData = pagination ? page < pagination.pageCount : false;
                    page += 1;
                }
                //console.log(allProjects)
                const shouldFilterByService = Boolean(service) && service !== 'all';
                // Filter projects by user ownership and, when available, by active service
                userProjects = allProjects
                    .filter(proj => ids.includes(proj.id))
                    .filter(proj => !shouldFilterByService || (proj?.attributes?.service) === service);
            }
            //console.log(userProjects)
            // Sort projects alphabetically by title
            userProjects.sort(function (a, b) {
                var textA = a.attributes.title.toUpperCase();
                var textB = b.attributes.title.toUpperCase();
                return (textA < textB) ? -1 : (textA > textB) ? 1 : 0;
            });

            // Update state with fetched projects
            setProjects(userProjects);
            setUsage(getSpaceUsage(userProjects)); // Calculate and update storage usage

            // Update project availability flag
            if (userProjects.length > 0) {
                setUserHasNoProject(false);
            } else {
                setUserHasNoProject(true);
            }
            setProjectSelected(userProjects[0] || null); // Select first project by default

            // Calculate upload statistics from user activities
            const activities = Array.isArray(userStrapi?.data?.activities) ? userStrapi.data.activities : [];
            setGoUploadedThisMonth(calculateUploadedSizeInGoThisMonth(activities));
            setMaxGoUploadPerMonth(userStrapi?.data?.maxUploadPerMonth ?? 1);

            // Update cesium link counters if setters are provided
            if (setMaxLinkCesium) setMaxLinkCesium(userStrapi.data.maxLinkCesium || 0);
            if (setUsedCesiumLinks) setUsedCesiumLinks(getCesiumLinksUsed(userProjects));

            // Initialize cloud credentials and processing options
            if (getCredentials) getCredentials(jwtStrapi);
            if (retrieveProcessings) retrieveProcessings(jwtStrapi);

        } catch (error) {
            console.error("Failed to retrieve projects", error);
        } finally {
            setLoading(false);
        }
    };

    /**
     * Calculates total storage usage across all user projects
     * @param {Array} projects - Array of project objects
     * @returns {number} Total storage usage in gigabytes
     */
    const getSpaceUsage = (projects) => {
        let space = 0;
        for (let i = 0; i < projects.length; i += 1) {
            let proj = projects[i]?.attributes;
            const files = Array.isArray(proj?.data?.data) ? proj.data.data : [];
            for (let j = 0; j < files.length; j += 1) {
                let d = files[j];
                space += parseFloat(d.attributes.size); // Size in bytes
            }
        }
        return space / 1e9; // Convert bytes to gigabytes
    };

    /**
     * Calculates the total size of files uploaded by user in the current month
     * Used for enforcing monthly upload limits
     * @param {Array} activities - User's activity log from Strapi
     * @returns {number} Upload size in gigabytes for current month
     */
    const calculateUploadedSizeInGoThisMonth = (activities) => {
        const now = new Date();
        const currentMonth = now.getMonth();
        const currentYear = now.getFullYear();

        // Filter activities for the current month and sum their file sizes
        const safeActivities = Array.isArray(activities) ? activities : [];
        const totalOctets = safeActivities
            .filter(obj => {
                const createdDate = new Date(obj.createdAt);
                return (
                    createdDate.getMonth() === currentMonth &&
                    createdDate.getFullYear() === currentYear
                );
            })
            .reduce((sum, obj) => +sum + +(obj.size || 0), 0);

        // Convert the total size from octets to gigabytes using utility function
        const totalGo = formatSizeToGb(totalOctets);
        return totalGo;
    };

    return {
        projects,
        setProjects,
        projectSelected,
        setProjectSelected,
        userHasNoProject,
        setUserHasNoProject,
        GoUploadedThisMonth,
        setGoUploadedThisMonth,
        maxGoUploadPerMonth,
        setMaxGoUploadPerMonth,
        loading,
        setLoading,
        retrieveProjects
    };
};
